/**
 * Split post content into segments: plain text, hashtags, and URLs.
 * Returns array of { type: 'text' | 'hashtag' | 'url', value: string, href?: string }.
 * URLs are made clickable when there's no video/OG preview.
 */
const HASHTAG_REGEX = /(#[a-zA-Z0-9_]+)/g;
const MENTION_REGEX = /(@[a-zA-Z0-9_]+)/g;
// Match http(s) URLs; trim trailing punctuation for cleaner href
const URL_REGEX = /(https?:\/\/[^\s<>[\]()"']+)/g;

function trimTrailingPunctuation(url) {
  return url.replace(/[.,;:!?)]+$/, '');
}

export function parseContentWithHashtags(content) {
  if (!content || typeof content !== 'string') return [{ type: 'text', value: content || '' }];
  const segments = [];
  let lastIndex = 0;
  let m;
  const re = new RegExp(HASHTAG_REGEX.source, 'g');
  while ((m = re.exec(content)) !== null) {
    if (m.index > lastIndex) {
      segments.push({ type: 'text', value: content.slice(lastIndex, m.index) });
    }
    segments.push({ type: 'hashtag', value: m[1].slice(1).toLowerCase() }); // value without #
    lastIndex = re.lastIndex;
  }
  if (lastIndex < content.length) {
    segments.push({ type: 'text', value: content.slice(lastIndex) });
  }
  return segments.length ? segments : [{ type: 'text', value: content }];
}

/**
 * Parse content into text, hashtag, mention, and URL segments so links can be rendered clickable.
 */
export function parseContentWithHashtagsAndUrls(content) {
  if (!content || typeof content !== 'string') return [{ type: 'text', value: content || '' }];
  const matches = [];
  let m;
  const hashtagRe = new RegExp(HASHTAG_REGEX.source, 'g');
  while ((m = hashtagRe.exec(content)) !== null) {
    matches.push({ index: m.index, end: m.index + m[0].length, type: 'hashtag', value: m[1].slice(1).toLowerCase() });
  }
  const mentionRe = new RegExp(MENTION_REGEX.source, 'g');
  while ((m = mentionRe.exec(content)) !== null) {
    matches.push({ index: m.index, end: m.index + m[0].length, type: 'mention', value: m[1].slice(1) });
  }
  const urlRe = new RegExp(URL_REGEX.source, 'g');
  while ((m = urlRe.exec(content)) !== null) {
    const raw = m[1];
    const href = trimTrailingPunctuation(raw);
    const end = m.index + href.length;
    matches.push({ index: m.index, end, type: 'url', value: href, href });
    urlRe.lastIndex = m.index + raw.length;
  }
  matches.sort((a, b) => a.index - b.index);
  const segments = [];
  let lastIndex = 0;
  for (const match of matches) {
    if (match.index > lastIndex) {
      segments.push({ type: 'text', value: content.slice(lastIndex, match.index) });
    }
    if (match.type === 'hashtag') {
      segments.push({ type: 'hashtag', value: match.value });
    } else if (match.type === 'mention') {
      segments.push({ type: 'mention', value: match.value });
    } else {
      segments.push({ type: 'url', value: match.value, href: match.href });
    }
    lastIndex = match.end;
  }
  if (lastIndex < content.length) {
    segments.push({ type: 'text', value: content.slice(lastIndex) });
  }
  return segments.length ? segments : [{ type: 'text', value: content }];
}
